import { computed, Injectable, signal } from '@angular/core';
import type { Location } from '@vendasta/protos/atlas/v1/types';
import { Subject } from 'rxjs';
import type { Config } from './config.service';

export type BusinessNavHostMode = 'business-app' | 'external';
export type BusinessNavLinkTarget = '_self' | '_blank';
export type BusinessNavErrorReason = 'auth' | 'data' | 'config';

export interface BusinessNavHostError {
  reason: BusinessNavErrorReason;
  status?: number;
}

export interface BusinessNavDrawerState {
  open: boolean;
  overlay: boolean;
}

/** The seam a foreign host overrides to take over links, sign-out, and location selection; the default is inert. */
@Injectable({ providedIn: 'root' })
export class BusinessNavHostBridge {
  readonly mode = signal<BusinessNavHostMode>('business-app');
  readonly linkTarget = signal<BusinessNavLinkTarget>('_self');
  readonly signOutDisabled = signal(false);
  readonly drawerState = signal<BusinessNavDrawerState>({ open: false, overlay: false });
  readonly tokenChanged$ = new Subject<void>();

  readonly isExternal = computed(() => this.mode() === 'external');
  readonly effectiveLinkTarget = computed<BusinessNavLinkTarget>(() =>
    this.isExternal() && (this.linkTarget() === '_blank' || this.isFramed()) ? '_blank' : '_self',
  );
  readonly signOutHidden = computed(() => this.isExternal() && (this.signOutDisabled() || this.isFramed()));

  isFramed(): boolean {
    return typeof window !== 'undefined' && window.self !== window.top;
  }

  /** Returns true when the host consumed the sign-out; the nav then neither clears the session nor navigates. */
  signOut(_logoutUrl: string): boolean {
    return false;
  }

  /** Returns true when the host consumed the selection; the nav then touches neither the router nor window.location. */
  locationSelected(_location: Location, _config: Config): boolean {
    return false;
  }

  reportError(_error: BusinessNavHostError): void {
    return;
  }

  notifyTokenChanged(): void {
    this.tokenChanged$.next();
  }
}

export function isAbsoluteHttpUrl(url: string | null | undefined): boolean {
  return /^https?:\/\//i.test(url ?? '');
}
